import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ediphorDiagnosticsService } from '@/services/api/ediphorDiagnosticsService';
import { useDiagnosticsStore } from '@/store/useDiagnosticsStore';
import { ImpedanceDataSection } from './pipeline/ImpedanceDataSection';
import { ConfigurationSection } from './pipeline/ConfigurationSection';
import { ExecuteResultsSection } from './pipeline/ExecuteResultsSection';
import type { ImpedanceDataDto } from '../types';

interface TableRow {
  frequency: number | '';
  magnitude: number | '';
  phase: number | '';
}

export function PipelineTest() {
  // Baseline state
  const [baselineRows, setBaselineRows] = useState<TableRow[]>([
    { frequency: 10, magnitude: 97428.7, phase: 0.054 },
    { frequency: 100, magnitude: 90000, phase: 0.045 },
    { frequency: 1000, magnitude: 82000, phase: 0.036 },
    { frequency: 10000, magnitude: 78388.6, phase: 0.046 }
  ]);
  const [baselineName, setBaselineName] = useState('');

  // Measurement state
  const [measurementRows, setMeasurementRows] = useState<TableRow[]>([
    { frequency: 10, magnitude: 95000, phase: 0.052 },
    { frequency: 100, magnitude: 88000, phase: 0.043 },
    { frequency: 1000, magnitude: 80000, phase: 0.034 },
    { frequency: 10000, magnitude: 76000, phase: 0.044 }
  ]);
  const [measurementName, setMeasurementName] = useState('');

  // Configuration state
  const [pathogen, setPathogen] = useState('FLU_A');
  const [deviceId, setDeviceId] = useState('TEST_DEVICE_001');
  const [electrodeType, setElectrodeType] = useState('WP20_4CH');

  // Results state
  const [result, setResult] = useState<any>(null);
  const [transformedRequest, setTransformedRequest] = useState<any>(null);

  const { savedBaselines, savedMeasurements, addBaseline, addMeasurement, removeBaseline, removeMeasurement } =
    useDiagnosticsStore();

  // Convert table rows to ImpedanceDataDto
  const rowsToImpedanceData = (rows: TableRow[]): ImpedanceDataDto => {
    const validRows = rows.filter((row) => row.frequency !== '' && row.magnitude !== '' && row.phase !== '');
    return {
      frequency_Hz: validRows.map((r) => r.frequency as number),
      magnitude_Ohm: [validRows.map((r) => r.magnitude as number)],
      phase_Rad: [validRows.map((r) => r.phase as number)]
    };
  };

  // Convert ImpedanceDataDto to table rows
  const impedanceDataToRows = (data: ImpedanceDataDto): TableRow[] => {
    return data.frequency_Hz.map((freq, index) => ({
      frequency: freq,
      magnitude: data.magnitude_Ohm[0]?.[index] ?? '',
      phase: data.phase_Rad[0]?.[index] ?? ''
    }));
  };

  const handleSaveBaseline = () => {
    if (!baselineName.trim()) {
      alert('Please enter a name for the baseline');
      return;
    }
    const data = rowsToImpedanceData(baselineRows);
    addBaseline(baselineName, data);
    setBaselineName('');
    alert('Baseline saved!');
  };

  const handleSaveMeasurement = () => {
    if (!measurementName.trim()) {
      alert('Please enter a name for the measurement');
      return;
    }
    const data = rowsToImpedanceData(measurementRows);
    addMeasurement(measurementName, data);
    setMeasurementName('');
    alert('Measurement saved!');
  };

  const handleLoadBaseline = (id: string) => {
    const baseline = savedBaselines.find((b) => b.id === id);
    if (baseline) {
      setBaselineRows(impedanceDataToRows(baseline.data));
    }
  };

  const handleLoadMeasurement = (id: string) => {
    const measurement = savedMeasurements.find((m) => m.id === id);
    if (measurement) {
      setMeasurementRows(impedanceDataToRows(measurement.data));
    }
  };

  const pipelineMutation = useMutation({
    mutationFn: async () => {
      const baseline = rowsToImpedanceData(baselineRows);
      const measurement = rowsToImpedanceData(measurementRows);

      return ediphorDiagnosticsService.runPipeline({
        baseline,
        measurement,
        pathogen,
        deviceId: deviceId || undefined,
        electrodeType
      });
    },
    onSuccess: (data) => {
      setResult(data.result);
      setTransformedRequest(data.transformedRequest);
    },
    onError: (error: any) => {
      setResult({ error: error.message });
      setTransformedRequest(null);
    }
  });

  return (
    <div className="space-y-6">
      {/* Section 1: Impedance Data Input */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="text-2xl">📊</span>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Section 1: Impedance Data Input</h2>
        </div>

        <div className="space-y-6">
          <ImpedanceDataSection
            title="Baseline"
            rows={baselineRows}
            onRowsChange={setBaselineRows}
            savedItems={savedBaselines}
            currentName={baselineName}
            onNameChange={setBaselineName}
            onSave={handleSaveBaseline}
            onLoad={handleLoadBaseline}
            onDelete={removeBaseline}
          />

          <ImpedanceDataSection
            title="Measurement"
            rows={measurementRows}
            onRowsChange={setMeasurementRows}
            savedItems={savedMeasurements}
            currentName={measurementName}
            onNameChange={setMeasurementName}
            onSave={handleSaveMeasurement}
            onLoad={handleLoadMeasurement}
            onDelete={removeMeasurement}
          />
        </div>
      </div>

      {/* Section 2: Configuration */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="text-2xl">⚙️</span>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Section 2: Configuration</h2>
        </div>

        <ConfigurationSection
          pathogen={pathogen}
          onPathogenChange={setPathogen}
          deviceId={deviceId}
          onDeviceIdChange={setDeviceId}
          electrodeType={electrodeType}
          onElectrodeTypeChange={setElectrodeType}
        />
      </div>

      {/* Section 3: Execute & Results */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <span className="text-2xl">🚀</span>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Section 3: Execute & Results</h2>
        </div>

        <ExecuteResultsSection
          isLoading={pipelineMutation.isPending}
          onExecute={() => pipelineMutation.mutate()}
          result={result}
          transformedRequest={transformedRequest}
        />
      </div>
    </div>
  );
}


